/**
 * Save envelope (SCHEMA 2, v0.2 RFC §7). Fail-loud contract per ADR-003:
 * no save → fresh; corrupt/newer/malformed → SaveError naming fields; the
 * only wipe is the player's explicit New Game. SCHEMA 1→2 is the project's
 * first real migration (v0.1 saves must load equivalently).
 */

import { CARE_TIERS, DISCHARGE, SAVE, type CareTierId } from "@/data/tunables";
import { SATIRE_TOASTS } from "@/data/satire";
import { CARDS, CARD_SCENES } from "@/data/cards";
const KNOWN_CARD_IDS = new Set(CARDS.map((c) => c.id));
const CARD_SCENE_BY_ID = new Map(CARDS.map((c) => [c.id, c.scene] as const));
import { LETTERS } from "@/data/letters";
import { UPGRADES } from "@/data/upgrades";
import { CRIMINAL_UPGRADES, CRIMINAL_HYBRID_SPLIT } from "@/data/criminal";
import { newGame, type GameState } from "@/state/GameState";

export class SaveError extends Error {}

interface SaveEnvelope {
  version: number;
  savedAt: number;
  account: GameState["account"];
  run: GameState["run"];
}

export interface LoadedGame {
  state: GameState;
  offlineMs: number;
  fresh: boolean;
}

export function saveGame(state: GameState): void {
  const envelope: SaveEnvelope = {
    version: SAVE.version,
    savedAt: Date.now(),
    account: state.account,
    run: state.run,
  };
  localStorage.setItem(SAVE.key, JSON.stringify(envelope));
}

const PATIENT_STATES = new Set(["new", "progressing", "issue", "paused"]);
const HIGH_WATER_VALUES = new Set(["none", ...CARE_TIERS, "alumni"]);

/** account survives prestige — a malformed one corrupts EVERY future run. */
function validateAccountShape(account: Record<string, unknown>): void {
  const problems: string[] = [];
  if (account.facilityName !== null && typeof account.facilityName !== "string") {
    problems.push("account.facilityName");
  }
  if (typeof account.highWaterLegit !== "string" || !HIGH_WATER_VALUES.has(account.highWaterLegit)) {
    problems.push("account.highWaterLegit");
  }
  for (const field of ["reputationCapital", "streetKnowledge", "lifetimeGraduations"] as const) {
    if (typeof account[field] !== "number" || !Number.isFinite(account[field] as number)) {
      problems.push(`account.${field}`);
    }
  }
  // highWaterCriminal is a BOUNDED tier index (0..tierCount) — validate it as
  // such so a tampered non-integer can't yield a fractional criminal access gate.
  const maxTier = CRIMINAL_UPGRADES.reduce((m, u) => Math.max(m, u.tier), 0);
  if (
    typeof account.highWaterCriminal !== "number" ||
    !Number.isInteger(account.highWaterCriminal) ||
    (account.highWaterCriminal as number) < 0 ||
    (account.highWaterCriminal as number) > maxTier
  ) {
    problems.push("account.highWaterCriminal");
  }
  // SCHEMA 4: upgrades — known-id → integer level in [0, that id's maxLevel].
  // Bounds match exactly what purchaseUpgrade can ever produce (fail-loud on a
  // tampered/over-range level rather than letting an unbounded head-start in).
  const upgrades = account.upgrades as Record<string, unknown> | undefined;
  if (typeof upgrades !== "object" || upgrades === null || Array.isArray(upgrades)) {
    problems.push("account.upgrades");
  } else {
    const maxById = new Map<string, number>(UPGRADES.map((u) => [u.id, u.maxLevel]));
    for (const [id, level] of Object.entries(upgrades)) {
      const max = maxById.get(id);
      if (
        max === undefined ||
        typeof level !== "number" ||
        !Number.isInteger(level) ||
        level < 0 ||
        level > max
      ) {
        problems.push(`account.upgrades.${id}`);
      }
    }
  }
  if (problems.length > 0) {
    throw new SaveError(`save account-state is malformed at: ${problems.join(", ")} — refusing to reset`);
  }
}

function validateRunShape(run: Record<string, unknown>): void {
  const problems: string[] = [];
  for (const field of ["tick", "funding", "reputation", "heat", "dea", "nextId", "graduatedOut"] as const) {
    if (typeof run[field] !== "number" || !Number.isFinite(run[field] as number)) problems.push(`run.${field}`);
  }
  const rng = run.rng as Record<string, unknown> | undefined;
  if (typeof rng?.seed !== "number" || typeof rng?.state !== "number") problems.push("run.rng.{seed,state}");
  const office = run.office as Record<string, unknown> | undefined;
  if (typeof office?.recruiterLevel !== "number") problems.push("run.office.recruiterLevel");
  if (!Array.isArray(run.queue)) problems.push("run.queue");
  const TIER_ORDER = [...CARE_TIERS, "alumni"] as const;
  const avail = run.availableTiers as unknown[];
  // office-only start makes [] legal (v0.3); non-empty must be a canonical prefix
  if (!Array.isArray(avail) || avail.some((t, i) => t !== TIER_ORDER[i])) {
    problems.push("run.availableTiers");
  }
  const tiers = run.tiers as Record<string, Record<string, unknown>> | undefined;
  if (typeof tiers !== "object" || tiers === null) {
    problems.push("run.tiers");
  } else {
    for (const t of CARE_TIERS) {
      const policy = tiers[t]?.dischargePolicy;
      if (
        typeof tiers[t]?.automatorLevel !== "number" ||
        typeof policy !== "string" ||
        !(policy in DISCHARGE)
      ) {
        problems.push(`run.tiers.${t}`);
      }
    }
  }
  if (!Array.isArray(run.patients)) {
    problems.push("run.patients");
  } else {
    (run.patients as unknown[]).forEach((p, i) => {
      const pat = p as Record<string, unknown>;
      const ok =
        typeof pat?.id === "number" &&
        typeof pat?.bed === "number" &&
        typeof pat?.tier === "string" &&
        (CARE_TIERS as readonly string[]).includes(pat.tier as string) &&
        typeof pat?.progress === "number" &&
        pat.progress >= 0 &&
        pat.progress <= 100 &&
        typeof pat?.state === "string" &&
        PATIENT_STATES.has(pat.state as string) &&
        typeof pat?.issueHistory === "number" &&
        typeof pat?.relapseCount === "number";
      if (!ok) problems.push(`run.patients[${i}]`);
    });
  }
  const alumni = run.alumni as Record<string, unknown> | undefined;
  if (!Array.isArray(alumni?.roster) || typeof alumni?.coordinatorLevel !== "number") {
    problems.push("run.alumni");
  }
  if (!Array.isArray(run.incomeLog)) problems.push("run.incomeLog");
  // SCHEMA 3 fields
  const mailbox = run.mailbox as Record<string, unknown> | undefined;
  if (
    !Array.isArray(mailbox?.arrived) ||
    !Array.isArray(mailbox?.pending) ||
    typeof mailbox?.lastArrivalTick !== "number" ||
    !Number.isFinite(mailbox?.lastArrivalTick as number)
  ) {
    problems.push("run.mailbox");
  } else {
    const known = new Set(LETTERS.map((l) => l.id));
    (mailbox.arrived as unknown[]).forEach((i, idx) => {
      const inst = i as Record<string, unknown>;
      if (
        typeof inst?.id !== "string" ||
        !known.has(inst.id) ||
        typeof inst?.read !== "boolean" ||
        typeof inst?.arrivedTick !== "number" ||
        !Number.isFinite(inst?.arrivedTick as number)
      ) {
        problems.push(`run.mailbox.arrived[${idx}]`);
      }
    });
  }
  for (const field of ["issuesEver", "deathsEver"] as const) {
    if (typeof run[field] !== "number" || !Number.isFinite(run[field] as number)) problems.push(`run.${field}`);
  }
  if (Array.isArray(mailbox?.pending)) {
    const known2 = new Set(LETTERS.map((l) => l.id));
    (mailbox.pending as unknown[]).forEach((id, i) => {
      if (typeof id !== "string" || !known2.has(id)) problems.push(`run.mailbox.pending[${i}]`);
    });
  }
  for (const field of ["capacityBonus", "capacityMultiplier", "graduationsByTier"] as const) {
    const rec = run[field] as Record<string, unknown> | undefined;
    if (typeof rec !== "object" || rec === null || CARE_TIERS.some((t) => typeof rec[t] !== "number")) {
      problems.push(`run.${field}`);
    }
  }
  if (run.purchasable !== null) {
    const pu = run.purchasable as Record<string, unknown> | undefined;
    const validTier = typeof pu?.tier === "string" && HIGH_WATER_VALUES.has(pu.tier) && pu.tier !== "none";
    if (!validTier || typeof pu?.price !== "number" || !Number.isFinite(pu?.price as number)) {
      problems.push("run.purchasable");
    }
  }
  if (typeof run.markAtRunStart !== "string" || !HIGH_WATER_VALUES.has(run.markAtRunStart)) {
    problems.push("run.markAtRunStart");
  }
  // SCHEMA 4 fields
  if (typeof run.prestigeOffered !== "boolean") problems.push("run.prestigeOffered");
  // v0.10 review (leg-1 C2): the broker lockout is authoritative sim state —
  // a missing/NaN value would read as permanently-locked with NaN in the
  // error message (silent-wrong). Validate like every other run field.
  if (typeof run.brokerLockoutUntil !== "number" || !Number.isFinite(run.brokerLockoutUntil as number) || (run.brokerLockoutUntil as number) < 0) {
    problems.push("run.brokerLockoutUntil");
  }
  // v0.13: the event scheduler's state — every field deep-validated
  // (fail-loud; a malformed events block must never read as "no events").
  const ev = run.events as Record<string, unknown> | undefined;
  if (typeof ev !== "object" || ev === null) {
    problems.push("run.events");
  } else {
    const evRng = ev.rng as { seed?: unknown; state?: unknown } | undefined;
    if (
      typeof evRng !== "object" || evRng === null ||
      typeof evRng.seed !== "number" || !Number.isFinite(evRng.seed) ||
      typeof evRng.state !== "number" || !Number.isFinite(evRng.state)
    ) {
      problems.push("run.events.rng");
    }
    if (typeof ev.seq !== "number" || !Number.isInteger(ev.seq) || (ev.seq as number) < 0) problems.push("run.events.seq");
    if (!Array.isArray(ev.active)) problems.push("run.events.active");
    else {
      // One-per-tier + unique-id invariants (a schema-valid corrupt save
      // must not smuggle scheduler-impossible states — codex).
      const ids = (ev.active as Array<{ id?: unknown }>).map((l) => l.id);
      if (new Set(ids).size !== ids.length) problems.push("run.events.active (duplicate ids)");
      const tiers = (ev.active as Array<{ tier?: unknown }>).map((l) => l.tier);
      if (new Set(tiers).size !== tiers.length) problems.push("run.events.active (multiple drops per tier)");
      for (const [i, a2] of (ev.active as unknown[]).entries()) {
        const l = a2 as Record<string, unknown>;
        const ok =
          typeof l === "object" && l !== null &&
          typeof l.id === "number" && Number.isInteger(l.id) && (l.id as number) >= 0 &&
          (l.kind === "money" || l.kind === "contraband") &&
          typeof l.tier === "string" && (CARE_TIERS as readonly string[]).includes(l.tier as string) &&
          typeof l.item === "string" &&
          typeof l.value === "number" && Number.isFinite(l.value) && (l.value as number) >= 0 &&
          typeof l.lifeLeft === "number" && Number.isInteger(l.lifeLeft) && (l.lifeLeft as number) >= 0;
        if (!ok) problems.push(`run.events.active[${i}]`);
      }
    }
    if (typeof ev.cooldownLeft !== "object" || ev.cooldownLeft === null || Array.isArray(ev.cooldownLeft)) {
      problems.push("run.events.cooldownLeft");
    } else {
      // Keys must be known channels; values finite non-negative integers
      // (a corrupt counter must never read as a legal cooldown — codex).
      for (const [k, v] of Object.entries(ev.cooldownLeft as Record<string, unknown>)) {
        const knownKey =
          k === "satire" ||
          (k.startsWith("loot:") && (CARE_TIERS as readonly string[]).includes(k.slice(5))) ||
          (k.startsWith("card:") && (CARD_SCENES as readonly string[]).includes(k.slice(5)));
        if (!knownKey || typeof v !== "number" || !Number.isInteger(v) || v < 0) {
          problems.push(`run.events.cooldownLeft["${k}"]`);
        }
      }
    }
    const deck = ev.satireDeck;
    if (
      !Array.isArray(deck) ||
      !(deck as unknown[]).every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0 && n < SATIRE_TOASTS.length) ||
      new Set(deck as number[]).size !== (deck as number[]).length
    ) {
      problems.push("run.events.satireDeck");
    }
    if (!Array.isArray(ev.cards)) problems.push("run.events.cards");
    else {
      const cards = ev.cards as Array<Record<string, unknown> | null>;
      const cardIds = cards.map((c) => (c && typeof c === "object" ? c.id : undefined));
      if (new Set(cardIds).size !== cardIds.length) problems.push("run.events.cards (duplicate ids)");
      const cardScenes = cards.map((c) => (c && typeof c === "object" ? c.scene : undefined));
      if (new Set(cardScenes).size !== cardScenes.length) problems.push("run.events.cards (multiple cards per scene)");
      for (const [i, c0] of (ev.cards as unknown[]).entries()) {
        const c = c0 as Record<string, unknown>;
        const ok =
          typeof c === "object" && c !== null && !Array.isArray(c) &&
          typeof c.id === "number" && Number.isInteger(c.id) && (c.id as number) >= 0 &&
          typeof c.defId === "string" && KNOWN_CARD_IDS.has(c.defId) &&
          typeof c.scene === "string" && (CARD_SCENES as readonly string[]).includes(c.scene as string) &&
          CARD_SCENE_BY_ID.get(c.defId as string) === c.scene && // scene must match the def (codex)
          typeof c.ticksLeft === "number" && Number.isInteger(c.ticksLeft) && (c.ticksLeft as number) >= 0;
        if (!ok) problems.push(`run.events.cards[${i}]`);
      }
    }
  }
  // peakNetPerSec is a monotonic high-water starting at 0 — never negative.
  if (typeof run.peakNetPerSec !== "number" || !Number.isFinite(run.peakNetPerSec as number) || (run.peakNetPerSec as number) < 0) {
    problems.push("run.peakNetPerSec");
  }
  // SCHEMA 5 fields (v0.5) + SCHEMA 10's fixerLevel (v0.15) — all
  // non-negative counters/levels.
  if (typeof run.brokerUnlocked !== "boolean") problems.push("run.brokerUnlocked");
  for (const field of ["brokerHistory", "prFirmLevel", "complianceOfficerLevel", "fixerLevel", "deathsPending"] as const) {
    if (typeof run[field] !== "number" || !Number.isFinite(run[field] as number) || (run[field] as number) < 0) {
      problems.push(`run.${field}`);
    }
  }
  // SCHEMA 6 fields (v0.6 criminal track).
  const criminal = run.criminal as Record<string, unknown> | undefined;
  if (typeof criminal !== "object" || criminal === null) {
    problems.push("run.criminal");
  } else {
    if (typeof criminal.unlocked !== "boolean") problems.push("run.criminal.unlocked");
    if (
      typeof criminal.tierAccess !== "number" ||
      !Number.isInteger(criminal.tierAccess) ||
      criminal.tierAccess < 0 ||
      criminal.tierAccess > CRIMINAL_UPGRADES.reduce((m, u) => Math.max(m, u.tier), 0)
    ) {
      problems.push("run.criminal.tierAccess");
    }
    const crUp = criminal.upgrades as Record<string, unknown> | undefined;
    if (typeof crUp !== "object" || crUp === null || Array.isArray(crUp)) {
      problems.push("run.criminal.upgrades");
    } else {
      const crMax = new Map<string, number>(CRIMINAL_UPGRADES.map((u) => [u.id, u.maxLevel]));
      for (const [id, level] of Object.entries(crUp)) {
        const max = crMax.get(id);
        if (max === undefined || typeof level !== "number" || !Number.isInteger(level) || level < 0 || level > max) {
          problems.push(`run.criminal.upgrades.${id}`);
        }
      }
    }
  }
  if (problems.length > 0) {
    throw new SaveError(`save run-state is malformed at: ${problems.join(", ")} — refusing to reset`);
  }
}

/**
 * SCHEMA 1 → 2: v0.1's single-tier world becomes the continuum. detox
 * patients gain tier:"detox"; run.detox splits into tiers.detox + the new
 * travel-era fields with fresh defaults.
 */
function migrateV1toV2(env: Record<string, unknown>): SaveEnvelope {
  const run = { ...(env.run as Record<string, unknown>) };
  const detox = run.detox as Record<string, unknown> | undefined;
  if (typeof detox !== "object" || detox === null || !Array.isArray(detox.patients)) {
    throw new SaveError('v1 save field "run.detox" is missing or malformed — refusing to reset');
  }
  if (typeof detox.technicianLevel !== "number" || !Number.isFinite(detox.technicianLevel)) {
    throw new SaveError('v1 save field "run.detox.technicianLevel" is missing or not a number — refusing to reset');
  }
  const patients = detox.patients.map((p) => ({
    ...(p as Record<string, unknown>),
    tier: "detox" as CareTierId,
  }));
  const tiers: Record<string, unknown> = {};
  for (const t of CARE_TIERS) {
    tiers[t] = {
      automatorLevel: t === "detox" ? (detox.technicianLevel as number) : 0,
      dischargePolicy: "standard",
    };
  }
  delete run.detox;
  const account = env.account as Record<string, unknown>;
  return {
    version: 2,
    savedAt: env.savedAt as number,
    account: {
      ...account,
      lifetimeGraduations:
        typeof account.lifetimeGraduations === "number" ? account.lifetimeGraduations : 0,
    } as SaveEnvelope["account"],
    run: {
      ...run,
      tiers,
      patients,
      availableTiers: ["detox"],
      alumni: { roster: [], contactBoostUntil: 0, coordinatorLevel: 0, repFraction: 0 },
      graduatedOut: 0,
    } as unknown as SaveEnvelope["run"],
  };
}

/**
 * SCHEMA 2 → 3 (v0.3): mailbox + capacity + counters + expansion state.
 * The world must not regress: gate letters for already-open tiers seed as
 * arrived+read; markAtRunStart = the deepest open care tier (generous — the
 * next offer is eligible, preserving a stub-era run's progression feel).
 */
function migrateV2toV3(env: SaveEnvelope): SaveEnvelope {
  const run = env.run as unknown as Record<string, unknown>;
  const open = (run.availableTiers as string[] | undefined) ?? [];
  const zero: Record<string, number> = {};
  const one: Record<string, number> = {};
  for (const t of CARE_TIERS) {
    zero[t] = 0;
    one[t] = 1;
  }
  const seeded: Array<{ id: string; arrivedTick: number; read: boolean }> = [];
  // welcome is the NAMING letter — only pre-read it if the facility is already
  // named; an unnamed migrated save must still get the naming prompt.
  const alreadyNamed = typeof (env.account as unknown as Record<string, unknown>).facilityName === "string";
  if (alreadyNamed) seeded.push({ id: "welcome", arrivedTick: 0, read: true });
  if (open.includes("detox")) seeded.push({ id: "accreditation", arrivedTick: 0, read: true });
  for (const t of open) {
    if (t !== "detox") seeded.push({ id: `expansionOffer-${t}`, arrivedTick: 0, read: true });
  }
  const careOpen = open.filter((t) => t !== "alumni");
  const mark = open.includes("alumni") ? "alumni" : (careOpen[careOpen.length - 1] ?? "none");
  return {
    ...env,
    version: 3,
    run: {
      ...(run as object),
      mailbox: { arrived: seeded, pending: [], lastArrivalTick: 0 },
      capacityBonus: { ...zero },
      capacityMultiplier: { ...one },
      graduationsByTier: { ...zero },
      issuesEver: 0,
      deathsEver: 0,
      markAtRunStart: mark,
      purchasable: null,
    } as unknown as SaveEnvelope["run"],
  };
}

/**
 * SCHEMA 3 → 4 (v0.4): prestige. Adds account.upgrades (empty — no prestige
 * shop history yet) and run.{prestigeOffered, peakNetPerSec}. A migrated run
 * has not read the "Sell the Facility" letter and carries no peak yet, so both
 * default to their fresh values; existing reputationCapital is preserved.
 */
function migrateV3toV4(env: SaveEnvelope): SaveEnvelope {
  const account = env.account as unknown as Record<string, unknown>;
  const run = env.run as unknown as Record<string, unknown>;
  return {
    ...env,
    version: 4,
    account: {
      ...(account as object),
      upgrades: {},
    } as unknown as SaveEnvelope["account"],
    run: {
      ...(run as object),
      prestigeOffered: false,
      peakNetPerSec: 0,
    } as unknown as SaveEnvelope["run"],
  };
}

/**
 * SCHEMA 4 → 5 (v0.5 Consequences): the broker, mitigation staff, and the
 * batched Death-Letter debt. All new run fields default to their fresh values
 * (no broker unlocked, no history, no staff, no pending deaths); existing
 * heat/rep/dea are untouched.
 */
function migrateV4toV5(env: SaveEnvelope): SaveEnvelope {
  const run = env.run as unknown as Record<string, unknown>;
  return {
    ...env,
    version: 5,
    run: {
      ...(run as object),
      brokerUnlocked: false,
      brokerHistory: 0,
      prFirmLevel: 0,
      complianceOfficerLevel: 0,
      deathsPending: 0,
    } as unknown as SaveEnvelope["run"],
  };
}

/**
 * SCHEMA 5 → 6 (v0.6 The Other Business): the criminal track. Adds
 * `run.criminal` (locked, no tiers, no upgrades). `account.streetKnowledge` +
 * `highWaterCriminal` already exist (reserved since v0.1) and are preserved.
 */
function migrateV5toV6(env: SaveEnvelope): SaveEnvelope {
  const run = env.run as unknown as Record<string, unknown>;
  return {
    ...env,
    version: 6,
    run: {
      ...(run as object),
      criminal: { unlocked: false, tierAccess: 0, upgrades: {} },
    } as unknown as SaveEnvelope["run"],
  };
}

/** v0.10 "The Control Room": the broker gains an authoritative tick
 * lockout. Existing saves start ready (0). */
function migrateV6toV7(env: SaveEnvelope): SaveEnvelope {
  const run = env.run as unknown as Record<string, unknown>;
  return {
    ...env,
    version: 7,
    run: {
      ...(run as object),
      brokerLockoutUntil: 0,
    } as unknown as SaveEnvelope["run"],
  };
}

/** v0.13 "The Texture": the event scheduler's state joins the run —
 * its own rng stream (derived deterministically from the run seed at
 * migration time is impossible — the original seed is gone — so we
 * derive from the CURRENT rng state's counter-mix; fresh-but-seeded),
 * empty active list, no cooldowns. */
function migrateV7toV8(env: SaveEnvelope): SaveEnvelope {
  const run = env.run as unknown as Record<string, unknown>;
  const mainRng = (run.rng as { seed?: unknown } | undefined)?.seed;
  const seedBasis = typeof mainRng === "number" && Number.isFinite(mainRng) ? (mainRng as number) : 1;
  const evSeed = ((seedBasis ^ 0x9e3779b9) >>> 0) || 1;
  return {
    ...env,
    version: 8,
    run: {
      ...(run as object),
      events: {
        rng: { seed: evSeed, state: evSeed >>> 0 },
        seq: 0,
        active: [],
        cooldownLeft: {},
        satireDeck: [],
      },
    } as unknown as SaveEnvelope["run"],
  };
}

/** v0.14 "The Event Card": choice cards join the scheduler state. */
function migrateV8toV9(env: SaveEnvelope): SaveEnvelope {
  const run = env.run as unknown as Record<string, unknown>;
  const events = { ...(run.events as object) } as Record<string, unknown>;
  events.cards = [];
  return {
    ...env,
    version: 9,
    run: { ...(run as object), events } as unknown as SaveEnvelope["run"],
  };
}

/**
 * v0.15 "The Fixer": adds `run.fixerLevel` (the DEA-axis mitigation role) and
 * DECOUPLES the criminal tree. Each old hybrid (income + deaMitigate) split
 * into a pure earner (kept its id) + a new pure-mitigator sibling; §8.5 grants
 * an existing owner BOTH halves at their old level — the earner already carries
 * the level under the same id, so we copy it onto the new sibling id too. No
 * one loses what they bought.
 */
function migrateV9toV10(env: SaveEnvelope): SaveEnvelope {
  const run = env.run as unknown as Record<string, unknown>;
  const criminal = { ...((run.criminal as object) ?? {}) } as Record<string, unknown>;
  const upgrades = { ...((criminal.upgrades as Record<string, number>) ?? {}) };
  for (const [oldId, siblingId] of Object.entries(CRIMINAL_HYBRID_SPLIT)) {
    const level = upgrades[oldId];
    // Idempotent: grant the sibling the earner's level only if it isn't already
    // present. A genuine v9 save can't carry the (SCHEMA-10-new) sibling id, so
    // this normally always fires; the guard keeps the step safe against a
    // re-run or a hand-edited save that already placed a sibling value.
    if (typeof level === "number" && level > 0 && upgrades[siblingId] === undefined) {
      upgrades[siblingId] = level;
    }
  }
  criminal.upgrades = upgrades;
  return {
    ...env,
    version: 10,
    run: { ...(run as object), fixerLevel: 0, criminal } as unknown as SaveEnvelope["run"],
  };
}

function migrate(env: SaveEnvelope): SaveEnvelope {
  let cur: SaveEnvelope = env;
  if (cur.version === 1) {
    cur = migrateV1toV2(cur as unknown as Record<string, unknown>);
    cur.version = 2;
  }
  if (cur.version === 2) {
    cur = migrateV2toV3(cur);
  }
  if (cur.version === 3) {
    cur = migrateV3toV4(cur);
  }
  if (cur.version === 4) {
    cur = migrateV4toV5(cur);
  }
  if (cur.version === 5) {
    cur = migrateV5toV6(cur);
  }
  if (cur.version === 6) {
    cur = migrateV6toV7(cur);
  }
  if (cur.version === 7) {
    cur = migrateV7toV8(cur);
  }
  if (cur.version === 8) {
    cur = migrateV8toV9(cur);
  }
  if (cur.version === 9) {
    cur = migrateV9toV10(cur);
  }
  return cur;
}

export function loadGame(newGameSeed: number): LoadedGame {
  const raw = localStorage.getItem(SAVE.key);
  if (raw === null) {
    return { state: newGame(newGameSeed), offlineMs: 0, fresh: true };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new SaveError(`save at localStorage["${SAVE.key}"] is not valid JSON — refusing to reset it`);
  }
  const env = parsed as Partial<SaveEnvelope> & { state?: unknown };
  // Pre-release exploration artifacts ({version, state}) — narrowly detected,
  // discarded as fresh (they predate the account/run contract entirely).
  if (env.state !== undefined && env.run === undefined && env.account === undefined) {
    console.info("halfway: discarding pre-release exploration save (legacy {version, state} shape)");
    return { state: newGame(newGameSeed), offlineMs: 0, fresh: true };
  }
  if (typeof env.version !== "number" || !Number.isInteger(env.version) || env.version < 1) {
    throw new SaveError(`save field "version" must be an integer >= 1 — refusing to reset`);
  }
  if (env.version > SAVE.version) {
    throw new SaveError(`save version ${env.version} is newer than this build understands (${SAVE.version})`);
  }
  if (typeof env.account !== "object" || env.account === null || typeof env.run !== "object" || env.run === null) {
    throw new SaveError(`save fields "account"/"run" are missing or malformed — refusing to reset`);
  }
  if (typeof env.savedAt !== "number" || !Number.isFinite(env.savedAt)) {
    throw new SaveError(`save field "savedAt" is missing or not a finite number — refusing to reset`);
  }
  const migrated = migrate(env as SaveEnvelope);
  validateAccountShape(migrated.account as unknown as Record<string, unknown>);
  validateRunShape(migrated.run as unknown as Record<string, unknown>);
  return {
    state: { account: migrated.account, run: migrated.run },
    offlineMs: Date.now() - migrated.savedAt,
    fresh: false,
  };
}

/** Explicit wipe — the ONLY sanctioned reset path (player-initiated New Game). */
export function clearSave(): void {
  localStorage.removeItem(SAVE.key);
}
